import { loadPdfjs } from "../../../../../shared/frontend/components/pdfjsLoader";

/** Millimeterangaben auf eine Nachkommastelle runden. */
const MM_DECIMAL_SCALE = 10;

/**
 * Druck von PDF-Dokumenten.
 *
 * Ein PDF laesst sich im Browser **nicht** ueber ein `<iframe src="blob:…">` drucken: Chrome
 * rendert PDFs in einem eigenen Prozess (`chrome-extension://…/pdf_embedder`), das umgebende
 * Dokument im iframe bleibt leer. `iframe.contentWindow.print()` findet dort nichts zu drucken
 * und faellt auf das Hauptdokument zurueck — im Druckdialog erscheint dann die Anwendung mit
 * einem PDF-Platzhalter statt des Dokuments.
 *
 * Deshalb wird das PDF hier mit pdf.js seitenweise gerendert und als Bildstrecke in ein echtes
 * HTML-Dokument gelegt. Dessen `contentWindow.print()` druckt nachweislich nur den Frame-Inhalt.
 */

/** Zielaufloesung der gerenderten Seiten. 150 dpi ist der Kompromiss aus Schriftschaerfe und Speicher. */
const PRINT_TARGET_DPI = 150;
const PDF_POINTS_PER_INCH = 72;
const PDF_POINTS_PER_MM = 25.4 / PDF_POINTS_PER_INCH;
const PRINT_RENDER_SCALE = PRINT_TARGET_DPI / PDF_POINTS_PER_INCH;
/** Notbremse, falls `afterprint` ausbleibt (z. B. wenn der Dialog vom System abgebrochen wird). */
const PRINT_CLEANUP_FALLBACK_MS = 60_000;
const PDF_HEADER_BYTES = [0x25, 0x50, 0x44, 0x46] as const;

export const PDF_CONTENT_TYPE = "application/pdf";

/** Erkennt PDFs auch dann, wenn der Server `application/octet-stream` liefert. */
export async function isPdfBlob(blob: Blob): Promise<boolean> {
  if ((blob.type || "").toLowerCase().split(";")[0].trim() === PDF_CONTENT_TYPE) return true;
  const header = new Uint8Array(await blob.slice(0, PDF_HEADER_BYTES.length).arrayBuffer());
  return PDF_HEADER_BYTES.every((byte, index) => header[index] === byte);
}

type PrintPdfBlobOptions = {
  /** Titel des Druckdokuments — erscheint je nach Browser als Standard-Dateiname im Dialog. */
  title: string;
};

type RenderedPrintPage = {
  objectUrl: string;
  widthMm: number;
  heightMm: number;
};

export async function printPdfBlob(blob: Blob, { title }: PrintPdfBlobOptions): Promise<boolean> {
  let pages: RenderedPrintPage[] = [];
  try {
    pages = await renderPdfPagesToImages(blob);
    if (pages.length === 0) throw new Error("PDF has no printable pages.");

    const frame = createHiddenPrintFrame();
    const frameWindow = frame.contentWindow;
    const frameDocument = frame.contentDocument;
    if (!frameWindow || !frameDocument) {
      frame.remove();
      throw new Error("Print frame window not available.");
    }

    frameDocument.open();
    frameDocument.write(buildPrintDocumentHtml(title, pages));
    frameDocument.close();

    await waitForImages(frameDocument);

    const cleanup = createOnceRunner(() => {
      pages.forEach((page) => URL.revokeObjectURL(page.objectUrl));
      frame.remove();
    });
    frameWindow.addEventListener("afterprint", () => window.setTimeout(cleanup, 0));
    window.setTimeout(cleanup, PRINT_CLEANUP_FALLBACK_MS);

    frameWindow.focus();
    frameWindow.print();
    return true;
  } catch (error) {
    // Ohne diese Ausgabe ist im Browser-Log nicht erkennbar, woran der Druck gescheitert ist —
    // der Fehler-Toast allein sagt nichts ueber die Ursache.
    console.error("PDF konnte nicht gedruckt werden", error);
    pages.forEach((page) => URL.revokeObjectURL(page.objectUrl));
    return false;
  }
}

async function renderPdfPagesToImages(blob: Blob): Promise<RenderedPrintPage[]> {
  const data = new Uint8Array(await blob.arrayBuffer());
  const pdfjs = await loadPdfjs();
  const loadingTask = pdfjs.getDocument({ data });
  const pages: RenderedPrintPage[] = [];

  try {
    const pdfDocument = await loadingTask.promise;
    // Bewusst sequenziell: bei mehrseitigen Auswertungen liegen sonst alle Seiten-Canvas
    // gleichzeitig im Speicher.
    for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
      const page = await pdfDocument.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: PRINT_RENDER_SCALE });

      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const context = canvas.getContext("2d", { alpha: false });
      if (!context) throw new Error("Canvas context unavailable.");
      context.fillStyle = "#fff";
      context.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvas, canvasContext: context, viewport }).promise;

      const pageBlob = await canvasToBlob(canvas);
      // Der Canvas darf erst nach dem Blob freigegeben werden, sonst faellt die Seite leer aus.
      canvas.width = 0;
      canvas.height = 0;

      pages.push({
        objectUrl: URL.createObjectURL(pageBlob),
        widthMm: baseViewport.width * PDF_POINTS_PER_MM,
        heightMm: baseViewport.height * PDF_POINTS_PER_MM
      });
    }
    return pages;
  } catch (error) {
    pages.forEach((page) => URL.revokeObjectURL(page.objectUrl));
    throw error;
  } finally {
    try {
      await loadingTask.destroy();
    } catch (error) {
      // Ein Freigabefehler darf weder den ursprünglichen Renderfehler überschreiben
      // noch erfolgreich erzeugte Druckbilder nachträglich ungültig machen.
      console.error("PDF-Ressourcen konnten nicht freigegeben werden", error);
    }
  }
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) resolve(result);
      else reject(new Error("Canvas could not be converted to an image."));
    }, "image/png");
  });
}

function buildPrintDocumentHtml(title: string, pages: RenderedPrintPage[]): string {
  // Das Blattformat kommt von der ersten Seite; gemischte Seitengroessen in einem Dokument
  // kommen in Auswertungen und Briefen nicht vor.
  const firstPage = pages[0]!;
  const images = pages
    .map((page) => `<img src="${page.objectUrl}" alt="">`)
    .join("\n    ");

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: ${formatMm(firstPage.widthMm)}mm ${formatMm(firstPage.heightMm)}mm; margin: 0; }
    html, body { margin: 0; padding: 0; background: #fff; }
    /* Die Rastermaße werden auf ganze Pixel abgerundet. Ihre natürliche Proportion kann
       deshalb über die Blatthöhe hinausragen und eine leere Folgeseite erzeugen. Beide
       Druckmaße kommen wie das Blattformat aus dem PDF, nicht aus den Bildpixeln. */
    img { display: block; width: ${formatMm(firstPage.widthMm)}mm; height: ${formatMm(firstPage.heightMm)}mm; break-inside: avoid; page-break-inside: avoid; break-after: page; page-break-after: always; }
    img:last-of-type { break-after: auto; page-break-after: auto; }
  </style>
</head>
<body>
    ${images}
</body>
</html>`;
}

async function waitForImages(frameDocument: Document): Promise<void> {
  const images = Array.from(frameDocument.images);
  await Promise.all(images.map(async (image) => {
    if (image.complete && image.naturalWidth > 0) return;
    await new Promise<void>((resolve) => {
      // Auch ein fehlgeschlagenes Bild loest auf: eine fehlende Seite ist kein Grund, den
      // gesamten Druck zu blockieren.
      image.addEventListener("load", () => resolve(), { once: true });
      image.addEventListener("error", () => resolve(), { once: true });
    });
  }));
}

function createHiddenPrintFrame(): HTMLIFrameElement {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.title = "";
  // Nicht `display: none` — ein nicht gerendertes iframe hat in Chrome keinen Druckkontext.
  frame.style.position = "fixed";
  frame.style.left = "-10000px";
  frame.style.top = "0";
  frame.style.width = "210mm";
  frame.style.height = "297mm";
  frame.style.border = "0";
  document.body.appendChild(frame);
  return frame;
}

function createOnceRunner(action: () => void): () => void {
  let done = false;
  return () => {
    if (done) return;
    done = true;
    action();
  };
}

function formatMm(value: number): string {
  return (Math.round(value * MM_DECIMAL_SCALE) / MM_DECIMAL_SCALE).toString();
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
